import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import type { InsertBuilding, InsertApartment, InsertSection, InsertChecklistItem, InsertPhotoRequirement, InsertCleaningTask, InsertCleaningJob } from "@shared/routes";
import { z } from "zod";

// === BUILDINGS ===
export function useBuildings() {
  return useQuery({
    queryKey: [api.buildings.list.path],
    queryFn: async () => {
      const res = await fetch(api.buildings.list.path);
      if (!res.ok) throw new Error("Failed to fetch buildings");
      return api.buildings.list.responses[200].parse(await res.json());
    },
  });
}

export function useCreateBuilding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertBuilding) => {
      const res = await fetch(api.buildings.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create building");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.buildings.list.path] }),
  });
}

export function useUpdateBuilding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertBuilding> }) => {
      const url = buildUrl(api.buildings.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update building");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.buildings.list.path] }),
  });
}

export function useDeleteBuilding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.buildings.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete building");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.buildings.list.path] }),
  });
}

// === APARTMENTS ===
export function useApartments() {
  return useQuery({
    queryKey: [api.apartments.list.path],
    queryFn: async () => {
      const res = await fetch(api.apartments.list.path);
      if (!res.ok) throw new Error("Failed to fetch apartments");
      return api.apartments.list.responses[200].parse(await res.json());
    },
  });
}

export function useCreateApartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertApartment) => {
      const res = await fetch(api.apartments.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create apartment");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.apartments.list.path] }),
  });
}

export function useUpdateApartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertApartment> }) => {
      const url = buildUrl(api.apartments.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = "Failed to update apartment";
        try { msg = JSON.parse(text)?.message || text || msg; } catch { msg = text || msg; }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.apartments.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.apartments.get.path] });
    },
  });
}

export function useDeleteApartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.apartments.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete apartment");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.apartments.list.path] }),
  });
}

export function useApartment(id: number) {
  return useQuery({
    queryKey: [api.apartments.get.path, id],
    queryFn: async () => {
      const url = buildUrl(api.apartments.get.path, { id });
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch apartment");
      return api.apartments.get.responses[200].parse(await res.json());
    },
    enabled: !!id,
  });
}

// === SECTIONS (CONTENT) ===
export function useAdminSections() {
  return useQuery({
    queryKey: [api.admin.sections.list.path],
    queryFn: async () => {
      const res = await fetch(api.admin.sections.list.path);
      if (!res.ok) throw new Error("Failed to fetch sections");
      return api.admin.sections.list.responses[200].parse(await res.json());
    },
  });
}

export function useCreateSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertSection) => {
      const res = await fetch(api.admin.sections.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create section");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.sections.list.path] }),
  });
}

export function useDeleteSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.sections.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete section");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.sections.list.path] }),
  });
}

// === CHECKLIST ITEMS ===
export function useCreateChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertChecklistItem) => {
      const res = await fetch(api.admin.checklist.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create checklist item");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.checklist.create.path] }),
  });
}

// === PHOTO REQUIREMENTS ===
export function useCreatePhotoRequirement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertPhotoRequirement) => {
      const res = await fetch(api.admin.photoRequirements.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create photo requirement");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.photoRequirements.create.path] }),
  });
}

// === SUBMISSIONS ===
export function useAdminSubmissions() {
  return useQuery({
    queryKey: [api.admin.submissions.list.path],
    queryFn: async () => {
      const res = await fetch(api.admin.submissions.list.path);
      if (!res.ok) throw new Error("Failed to fetch submissions");
      return api.admin.submissions.list.responses[200].parse(await res.json());
    },
  });
}

export function useDeleteSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.submissions.delete.path, { id });
      const res = await fetch(url, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete submission");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.submissions.list.path] });
    },
  });
}

// === CLEANING TASKS (legacy) ===
export function useCleaningTasks() {
  return useQuery({
    queryKey: [api.admin.cleaning.list.path],
    queryFn: async () => {
      const res = await fetch(api.admin.cleaning.list.path);
      if (!res.ok) throw new Error("Failed to fetch cleaning tasks");
      return api.admin.cleaning.list.responses[200].parse(await res.json());
    },
  });
}

export function useCreateCleaningTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<InsertCleaningTask>) => {
      const res = await fetch(api.admin.cleaning.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create cleaning task");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.cleaning.list.path] }),
  });
}

export function useUpdateCleaningTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertCleaningTask> }) => {
      const url = buildUrl(api.admin.cleaning.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update cleaning task");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.cleaning.list.path] }),
  });
}

export function useDeleteCleaningTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.cleaning.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete cleaning task");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.cleaning.list.path] }),
  });
}

// === CLEANING JOBS (new automated system) ===
export function useCleaningJobFinancialRecords(jobId: number | null) {
  return useQuery({
    queryKey: [`/api/admin/cleaning-jobs/${jobId}/financial-records`],
    queryFn: async () => {
      const res = await fetch(`/api/admin/cleaning-jobs/${jobId}/financial-records`);
      if (!res.ok) throw new Error("Failed to fetch financial records");
      return res.json() as Promise<any[]>;
    },
    enabled: jobId !== null,
  });
}

export function useCleaningJobs(page = 0, limit = 300, apartmentId?: number | null, dateFrom?: string | null, dateTo?: string | null) {
  return useQuery({
    queryKey: [api.admin.cleaningJobs.list.path, page, limit, apartmentId ?? null, dateFrom ?? null, dateTo ?? null],
    queryFn: async () => {
      const offset = page * limit;
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (apartmentId) params.set('apartmentId', String(apartmentId));
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const url = `${api.admin.cleaningJobs.list.path}?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch cleaning jobs");
      const jobs = await res.json();
      const total = parseInt(res.headers.get('X-Total-Count') || '0', 10);
      return { jobs, total };
    },
  });
}

export function useCreateCleaningJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { apartmentId?: number | null; reservationId?: number | null; assignedTo?: number | null; secondCleanerId?: number | null; priority?: string; notes?: string; address?: string | null; scheduledDate?: string; ownerUserId?: number | null; jobType?: string; adminNotes?: string | null; assignedOrgId?: number | null }) => {
      const res = await fetch(api.admin.cleaningJobs.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to create cleaning job" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.cleaningJobs.list.path] }),
  });
}

export function useUpdateCleaningJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertCleaningJob> }) => {
      const url = buildUrl(api.admin.cleaningJobs.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to update cleaning job" }));
        throw new Error(err.message || "Failed to update cleaning job");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.cleaningJobs.list.path] }),
  });
}

export function useDeleteCleaningJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.cleaningJobs.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete cleaning job");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.cleaningJobs.list.path] }),
  });
}

// === GENERAL TASKS ===
export function useGeneralTasks() {
  return useQuery({
    queryKey: [api.admin.generalTasks.list.path],
    queryFn: async () => {
      const res = await fetch(api.admin.generalTasks.list.path);
      if (!res.ok) throw new Error("Failed to fetch general tasks");
      return res.json();
    },
  });
}

type GeneralTaskPayload = {
  title?: string;
  description?: string | null;
  assignedTo?: number | null;
  scheduledAt?: string | null;
  address?: string | null;
  apartmentId?: number | null;
  taskType?: string;
  status?: string;
  billable?: boolean;
  billingAmount?: string | null;
  billedToUserId?: number | null;
  cleaningJobId?: number | null;
};

export function useCreateGeneralTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: GeneralTaskPayload) => {
      const res = await fetch(api.admin.generalTasks.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to create general task" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.generalTasks.list.path] }),
  });
}

export function useUpdateGeneralTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: GeneralTaskPayload }) => {
      const url = buildUrl(api.admin.generalTasks.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to update general task" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.generalTasks.list.path] }),
  });
}

export function useDeleteGeneralTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.generalTasks.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete general task");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.generalTasks.list.path] }),
  });
}

export function useCleanerGeneralTasks() {
  return useQuery({
    queryKey: [api.cleaner.generalTasks.list.path],
    queryFn: async () => {
      const res = await fetch(api.cleaner.generalTasks.list.path);
      if (!res.ok) throw new Error("Failed to fetch general tasks");
      return res.json();
    },
  });
}

export function useUpdateCleanerGeneralTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const url = buildUrl(api.cleaner.generalTasks.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed to update general task");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.cleaner.generalTasks.list.path] }),
  });
}

// === ADMIN USERS ===
export function useAdminUsers() {
  return useQuery({
    queryKey: [api.admin.users.list.path],
    queryFn: async () => {
      const res = await fetch(api.admin.users.list.path);
      if (!res.ok) throw new Error("Failed to fetch users");
      return res.json();
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.admin.users.create.input>) => {
      const res = await fetch(api.admin.users.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to create user");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.users.list.path] }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.users.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete user");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.users.list.path] }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number } & z.infer<typeof api.admin.users.update.input>) => {
      const url = buildUrl(api.admin.users.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to update user");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.users.list.path] }),
  });
}

// === OWNER PORTAL ===
export function useOwnerApartments() {
  return useQuery({
    queryKey: [api.owner.apartments.list.path],
    queryFn: async () => {
      const res = await fetch(api.owner.apartments.list.path);
      if (!res.ok) throw new Error("Failed to fetch apartments");
      return res.json();
    },
  });
}

export function useOwnerSubmissions() {
  return useQuery({
    queryKey: [api.owner.submissions.list.path],
    queryFn: async () => {
      const res = await fetch(api.owner.submissions.list.path);
      if (!res.ok) throw new Error("Failed to fetch submissions");
      return res.json();
    },
  });
}

export function useOwnerCleaningJobs() {
  return useQuery({
    queryKey: [api.owner.cleaningJobs.list.path],
    queryFn: async () => {
      const res = await fetch(api.owner.cleaningJobs.list.path);
      if (!res.ok) throw new Error("Failed to fetch cleaning jobs");
      return res.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes — job statuses change during the day
    refetchOnMount: true,
  });
}

export function useOwnerStats() {
  return useQuery({
    queryKey: [api.owner.stats.get.path],
    queryFn: async () => {
      const res = await fetch(api.owner.stats.get.path);
      if (!res.ok) throw new Error("Failed to fetch stats");
      return res.json();
    },
  });
}

// === CLEANER PORTAL ===
export function useCleanerJobs() {
  return useQuery({
    queryKey: [api.cleaner.jobs.list.path],
    queryFn: async () => {
      const res = await fetch(api.cleaner.jobs.list.path);
      if (!res.ok) throw new Error("Failed to fetch cleaning jobs");
      return res.json();
    },
  });
}

export function useUpdateCleanerJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { status: string; notes?: string } }) => {
      const url = buildUrl(api.cleaner.jobs.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update job");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.cleaner.jobs.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.cleaner.finances.records.path] });
      queryClient.invalidateQueries({ queryKey: [api.cleaner.finances.summary.path] });
    },
  });
}

export function useCleanerApartments() {
  return useQuery({
    queryKey: [api.cleaner.apartments.list.path],
    queryFn: async () => {
      const res = await fetch(api.cleaner.apartments.list.path);
      if (!res.ok) throw new Error("Failed to fetch apartments");
      return res.json();
    },
  });
}

// === ADMIN RESERVATIONS ===
export function useAdminReservations() {
  return useQuery({
    queryKey: [api.admin.reservations.list.path],
    queryFn: async () => {
      const res = await fetch(api.admin.reservations.list.path);
      if (!res.ok) throw new Error("Failed to fetch reservations");
      return res.json();
    },
  });
}

export class ReservationConflictError extends Error {
  status: number;
  overlaps: any[];
  constructor(message: string, overlaps: any[]) {
    super(message);
    this.status = 409;
    this.overlaps = overlaps;
  }
}

async function handleReservationResponse(res: Response) {
  if (res.status === 409) {
    const body = await res.json();
    throw new ReservationConflictError(body.message || "Overlapping reservation exists", body.overlaps || []);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to save reservation");
  }
  return res.json();
}

export function useCreateAdminReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.admin.reservations.create.input> & { _force?: boolean }) => {
      const { _force, ...body } = data;
      const url = _force ? `${api.admin.reservations.create.path}?force=true` : api.admin.reservations.create.path;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return handleReservationResponse(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.reservations.list.path] }),
  });
}

export function useUpdateAdminReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data, force }: { id: number; data: z.infer<typeof api.admin.reservations.update.input>; force?: boolean }) => {
      const base = buildUrl(api.admin.reservations.update.path, { id });
      const url = force ? `${base}?force=true` : base;
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return handleReservationResponse(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.reservations.list.path] }),
  });
}

export function useDeleteAdminReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.reservations.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete reservation");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.admin.reservations.list.path] }),
  });
}

export function useUpdateReservationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await fetch(`/api/admin/reservations/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to update status" }));
        throw new Error(err.message || "Failed to update status");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.reservations.list.path] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cleaning-jobs'] });
    },
  });
}

// === OWNER RESERVATIONS ===
export function useOwnerReservations() {
  return useQuery({
    queryKey: [api.owner.reservations.list.path],
    queryFn: async () => {
      const res = await fetch(api.owner.reservations.list.path);
      if (!res.ok) throw new Error("Failed to fetch reservations");
      return res.json();
    },
  });
}

export function useCreateOwnerReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.owner.reservations.create.input>) => {
      const res = await fetch(api.owner.reservations.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return handleReservationResponse(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.owner.reservations.list.path] }),
  });
}

export function useUpdateOwnerReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: z.infer<typeof api.owner.reservations.update.input> }) => {
      const url = buildUrl(api.owner.reservations.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return handleReservationResponse(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.owner.reservations.list.path] }),
  });
}

export function useDeleteOwnerReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.owner.reservations.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete reservation");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.owner.reservations.list.path] }),
  });
}

// === ADMIN FINANCES ===
export function useAdminFinancialRecords() {
  return useQuery({
    queryKey: [api.admin.finances.records.path],
    queryFn: async () => {
      const res = await fetch(api.admin.finances.records.path);
      if (!res.ok) throw new Error("Failed to fetch financial records");
      return res.json();
    },
  });
}

export function useAdminFinancialSummary() {
  return useQuery({
    queryKey: [api.admin.finances.summary.path],
    queryFn: async () => {
      const res = await fetch(api.admin.finances.summary.path);
      if (!res.ok) throw new Error("Failed to fetch financial summary");
      return res.json();
    },
  });
}

export function useCreateFinancialRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.admin.finances.create.input>) => {
      const res = await fetch(api.admin.finances.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create financial record");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.records.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.summary.path] });
    },
  });
}

export function useUpdateFinancialRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: z.infer<typeof api.admin.finances.update.input> }) => {
      const url = buildUrl(api.admin.finances.update.path, { id });
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update financial record");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.records.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.summary.path] });
    },
  });
}

export function useDeleteFinancialRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.admin.finances.delete.path, { id });
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete financial record");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.records.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.summary.path] });
    },
  });
}

export function useCommissionSettings() {
  return useQuery({
    queryKey: [api.admin.finances.settings.get.path],
    queryFn: async () => {
      const res = await fetch(api.admin.finances.settings.get.path);
      if (!res.ok) throw new Error("Failed to fetch commission settings");
      return res.json();
    },
  });
}

export function useUpdateCommissionSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.admin.finances.settings.update.input>) => {
      const res = await fetch(api.admin.finances.settings.update.path, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update commission settings");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.settings.get.path] });
    },
  });
}

// === OWNER FINANCES ===
export function useOwnerFinancialRecords() {
  return useQuery({
    queryKey: [api.owner.finances.records.path],
    queryFn: async () => {
      const res = await fetch(api.owner.finances.records.path);
      if (!res.ok) throw new Error("Failed to fetch financial records");
      return res.json();
    },
  });
}

export function useOwnerFinancialSummary() {
  return useQuery({
    queryKey: [api.owner.finances.summary.path],
    queryFn: async () => {
      const res = await fetch(api.owner.finances.summary.path);
      if (!res.ok) throw new Error("Failed to fetch financial summary");
      return res.json();
    },
  });
}

// === CLEANER FINANCES ===
export function useCleanerFinancialRecords() {
  return useQuery({
    queryKey: [api.cleaner.finances.records.path],
    queryFn: async () => {
      const res = await fetch(api.cleaner.finances.records.path);
      if (!res.ok) throw new Error("Failed to fetch financial records");
      return res.json();
    },
  });
}

export function useCleanerFinancialSummary() {
  return useQuery({
    queryKey: [api.cleaner.finances.summary.path],
    queryFn: async () => {
      const res = await fetch(api.cleaner.finances.summary.path);
      if (!res.ok) throw new Error("Failed to fetch financial summary");
      return res.json();
    },
  });
}

// === SECURITY DEPOSITS ===
export function useAdminDeposits() {
  return useQuery({
    queryKey: [api.admin.deposits.list.path],
    queryFn: async () => {
      const res = await fetch(api.admin.deposits.list.path);
      if (!res.ok) throw new Error("Failed to fetch deposits");
      return res.json();
    },
  });
}

export function useCreateDeposit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.admin.deposits.create.input>) => {
      const res = await fetch(api.admin.deposits.create.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create deposit");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.deposits.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.balance.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.transactions.path] });
    },
  });
}

export function useUpdateDeposit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: z.infer<typeof api.admin.deposits.update.input> }) => {
      const res = await fetch(`/api/admin/deposits/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update deposit");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.deposits.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.balance.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.transactions.path] });
    },
  });
}

// === CAJA (CASH BOX) ===
export function useCajaBalance() {
  return useQuery({
    queryKey: [api.admin.caja.balance.path],
    queryFn: async () => {
      const res = await fetch(api.admin.caja.balance.path);
      if (!res.ok) throw new Error("Failed to fetch caja balance");
      return res.json();
    },
  });
}

export function useCajaTransactions() {
  return useQuery({
    queryKey: [api.admin.caja.transactions.path],
    queryFn: async () => {
      const res = await fetch(api.admin.caja.transactions.path);
      if (!res.ok) throw new Error("Failed to fetch caja transactions");
      return res.json();
    },
  });
}

export function useLogCajaPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.admin.caja.logPayment.input>) => {
      const res = await fetch(api.admin.caja.logPayment.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to log payment");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.balance.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.transactions.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.reconciliation.path] });
    },
  });
}

export function useCajaSettle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: z.infer<typeof api.admin.caja.settle.input>) => {
      const res = await fetch(api.admin.caja.settle.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to settle with owner");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.balance.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.transactions.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.caja.reconciliation.path] });
    },
  });
}

export function useCajaReconciliation(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const queryString = params.toString();
  const url = queryString ? `${api.admin.caja.reconciliation.path}?${queryString}` : api.admin.caja.reconciliation.path;
  return useQuery({
    queryKey: [api.admin.caja.reconciliation.path, startDate, endDate],
    queryFn: async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch reconciliation");
      return res.json();
    },
  });
}

export function useOwnerDeposits() {
  return useQuery({
    queryKey: [api.owner.deposits.list.path],
    queryFn: async () => {
      const res = await fetch(api.owner.deposits.list.path);
      if (!res.ok) throw new Error("Failed to fetch deposits");
      return res.json();
    },
  });
}

export function useOwnerReconciliation(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const queryString = params.toString();
  const url = queryString ? `${api.owner.caja.reconciliation.path}?${queryString}` : api.owner.caja.reconciliation.path;
  return useQuery({
    queryKey: [api.owner.caja.reconciliation.path, startDate, endDate],
    queryFn: async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch reconciliation");
      return res.json();
    },
  });
}

// === QA VISION ===

export function useCleanerFeatures() {
  return useQuery({
    queryKey: [api.qa.cleanerFeatures.path],
    queryFn: async () => {
      const res = await fetch(api.qa.cleanerFeatures.path);
      if (!res.ok) return { qa_vision: false };
      return res.json() as Promise<{ qa_vision: boolean }>;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useQaInspection(jobId: number | null) {
  return useQuery({
    queryKey: ["/api/qa/inspection", jobId],
    queryFn: async () => {
      const res = await fetch(`/api/qa/inspection/${jobId}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch QA inspection");
      return res.json();
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as { zones?: { status: string }[] } | null;
      const hasScanning = data?.zones?.some((z) => z.status === "scanning");
      return hasScanning ? 5000 : false;
    },
  });
}

export function useStartQaInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) => {
      const res = await fetch(`/api/qa/inspections/${jobId}/start`, { method: "POST" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to start inspection" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/qa/inspection", jobId] });
    },
  });
}

export function useScanZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      inspectionId,
      zoneResultId,
      photos,
      jobId,
    }: {
      inspectionId: number;
      zoneResultId: number;
      photos: File[];
      jobId: number;
    }) => {
      const formData = new FormData();
      formData.append("inspectionId", String(inspectionId));
      formData.append("zoneResultId", String(zoneResultId));
      photos.forEach(f => formData.append("photos", f));

      const res = await fetch("/api/qa/scan", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Scan failed" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/qa/inspection", jobId] });
    },
  });
}

export function useQaReport(jobId: number | null) {
  return useQuery({
    queryKey: ["/api/qa/report", jobId],
    queryFn: async () => {
      const res = await fetch(`/api/qa/report/${jobId}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch QA report");
      return res.json();
    },
    enabled: !!jobId,
  });
}

export function useQaCriteria(unitId: number | null) {
  return useQuery({
    queryKey: ["/api/qa/criteria", unitId],
    queryFn: async () => {
      const res = await fetch(`/api/qa/criteria/${unitId}`);
      if (!res.ok) throw new Error("Failed to fetch QA criteria");
      return res.json();
    },
    enabled: !!unitId,
  });
}

// === QA ZONE TEMPLATES ===

export function useQaTemplates() {
  return useQuery({
    queryKey: ["/api/qa/templates"],
    queryFn: async () => {
      const res = await fetch("/api/qa/templates");
      if (!res.ok) throw new Error("Failed to fetch QA templates");
      return res.json() as Promise<any[]>;
    },
  });
}

export function useCreateQaTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; zonesJson: any[]; isDefault?: boolean }) => {
      const res = await fetch("/api/qa/templates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to create template");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/qa/templates"] }),
  });
}

export function useUpdateQaTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number; name: string; zonesJson: any[]; isDefault?: boolean }) => {
      const res = await fetch(`/api/qa/templates/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to update template");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/qa/templates"] }),
  });
}

export function useDeleteQaTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/qa/templates/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to delete template");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/qa/templates"] }),
  });
}

export function useSetDefaultQaTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/qa/templates/${id}/default`, { method: "PUT" });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to set default");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/qa/templates"] }),
  });
}

export function useApplyQaTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, unitIds }: { templateId: number; unitIds: number[] }) => {
      const res = await fetch(`/api/qa/templates/${templateId}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unitIds }),
      });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to apply template");
      return res.json() as Promise<{ applied: number }>;
    },
    onSuccess: (_, { unitIds }) => {
      unitIds.forEach(id => queryClient.invalidateQueries({ queryKey: ["/api/qa/criteria", id] }));
    },
  });
}

export function useClearUnitZones() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (unitId: number) => {
      const res = await fetch(`/api/qa/criteria/${unitId}/zones`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to clear zones");
      return res.json();
    },
    onSuccess: (_, unitId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/qa/criteria", unitId] });
    },
  });
}

export function useUpdateQaCriteria(unitId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (criteriaMap: Record<string, any>) => {
      const res = await fetch(`/api/qa/criteria/${unitId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(criteriaMap),
      });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to update criteria");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/qa/criteria", unitId] }),
  });
}

export function useQaOrgSettings() {
  return useQuery({
    queryKey: ["/api/qa/org-settings"],
    queryFn: async () => {
      const res = await fetch("/api/qa/org-settings");
      if (!res.ok) throw new Error("Failed to fetch QA settings");
      return res.json() as Promise<{
        passThreshold: number;
        rescanLimit: number;
        requireAllZones: boolean;
        allowOverrideFailed: boolean;
        autoMaintenance: boolean;
      }>;
    },
  });
}

export function useUpdateQaOrgSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<{
      passThreshold: number;
      rescanLimit: number;
      requireAllZones: boolean;
      allowOverrideFailed: boolean;
      autoMaintenance: boolean;
    }>) => {
      const res = await fetch("/api/qa/org-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to update QA settings");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/qa/org-settings"] }),
  });
}

// === CROSS-ORG CONTRACTS ===

export function useOrgContracts() {
  return useQuery({
    queryKey: ["/api/cross-org/contracts"],
    queryFn: async () => {
      const res = await fetch("/api/cross-org/contracts");
      if (!res.ok) throw new Error("Failed to fetch cross-org contracts");
      return res.json() as Promise<{
        asProvider: import("@shared/schema").OrgContract[];
        asConsumer: import("@shared/schema").OrgContract[];
      }>;
    },
  });
}

export function useOrgDataGrants(contractId: number | null) {
  return useQuery({
    queryKey: ["/api/cross-org/contracts", contractId, "grants"],
    enabled: contractId !== null,
    queryFn: async () => {
      const res = await fetch(`/api/cross-org/contracts/${contractId}/grants`);
      if (!res.ok) throw new Error("Failed to fetch grants");
      return res.json() as Promise<import("@shared/schema").OrgDataGrant[]>;
    },
  });
}

export function useContractSummary(contractId: number | null) {
  return useQuery({
    queryKey: ["/api/cross-org/contracts", contractId, "summary"],
    enabled: contractId !== null,
    queryFn: async () => {
      const res = await fetch(`/api/cross-org/contracts/${contractId}/summary`);
      if (!res.ok) throw new Error("Failed to fetch contract summary");
      return res.json() as Promise<{
        contract: import("@shared/schema").OrgContract;
        callerRole: 'provider' | 'consumer';
        counterpartyOrg: { id: number; name: string };
        apartments: Array<{ id: number; name: string; bedrooms: number | null }>;
        stats: {
          jobsCompleted: number;
          jobsPending: number;
          jobsCancelled: number;
          totalEarned: string;
          totalPending: string;
          totalInvoiced: string;
          totalOutstanding: string;
        };
        recentJobs: Array<{
          id: number;
          apartmentName: string;
          status: string;
          scheduledDate: string | null;
          completedAt: string | null;
          assigneeName: string | null;
        }>;
        invoices: Array<{
          id: number;
          invoiceNumber: string;
          status: string;
          subtotal: string;
          platformFeeAmount: string;
          total: string;
          periodStart: string;
          periodEnd: string;
        }>;
      }>;
    },
  });
}

export function useOrganizations() {
  return useQuery({
    queryKey: ["/api/cross-org/organizations"],
    queryFn: async () => {
      const res = await fetch("/api/cross-org/organizations");
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json() as Promise<import("@shared/schema").Organization[]>;
    },
  });
}

export function useProposeContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      counterpartyOrgId: number;
      callerRole: "provider" | "consumer";
      contractType: "service_engagement" | "internal_management";
      title: string;
      terms: Record<string, unknown>;
      endsAt: string;
      startsAt?: string;
    }) => {
      const res = await fetch("/api/cross-org/contracts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to propose contract" }));
        throw new Error(err.message ?? "Failed to propose contract");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/cross-org/contracts"] }),
  });
}

export function useAcceptContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, additionalTerms }: { id: number; additionalTerms?: Record<string, unknown> }) => {
      const res = await fetch(`/api/cross-org/contracts/${id}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ additionalTerms }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to accept contract" }));
        throw new Error(err.message ?? "Failed to accept contract");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/cross-org/contracts"] }),
  });
}

export function useRejectContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason?: string }) => {
      const res = await fetch(`/api/cross-org/contracts/${id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to reject contract" }));
        throw new Error(err.message ?? "Failed to reject contract");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/cross-org/contracts"] }),
  });
}

export function useTerminateContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason?: string }) => {
      const res = await fetch(`/api/cross-org/contracts/${id}/terminate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to terminate contract" }));
        throw new Error(err.message ?? "Failed to terminate contract");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/cross-org/contracts"] }),
  });
}

// === INVOICE DASHBOARD ===

type InvoiceDashboardFilters = { from: string; to: string };

export function useInvoiceDashboard(filters: InvoiceDashboardFilters) {
  return useQuery({
    queryKey: ['/api/admin/owner-invoices/dashboard', filters.from, filters.to],
    queryFn: async () => {
      const params = new URLSearchParams({ from: filters.from, to: filters.to });
      const res = await fetch(`/api/admin/owner-invoices/dashboard?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load invoice dashboard');
      return res.json();
    },
    staleTime: 60_000,
  });
}

type ReceivablesParams = {
  sortBy: string;
  sortDir: 'asc' | 'desc';
  search: string;
  minDaysOverdue: number;
};

export function useReceivables(params: ReceivablesParams) {
  return useQuery({
    queryKey: ['/api/admin/owner-invoices/receivables', params],
    queryFn: async () => {
      const q = new URLSearchParams({
        sortBy: params.sortBy,
        sortDir: params.sortDir,
        ...(params.search ? { search: params.search } : {}),
        ...(params.minDaysOverdue > 0 ? { minDaysOverdue: String(params.minDaysOverdue) } : {}),
      });
      const res = await fetch(`/api/admin/owner-invoices/receivables?${q}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load receivables');
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useBulkGenerateInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { periodStart: string; periodEnd: string; ownerIds?: number[] }) => {
      const res = await fetch('/api/admin/owner-invoices/bulk-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Failed' }));
        throw new Error(err.message);
      }
      return res.json() as Promise<{ created: number; skipped: number; errors: string[] }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/owner-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/owner-invoices/receivables'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/owner-invoices/dashboard'] });
    },
  });
}
